import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import BrandLogo from '../components/BrandLogo';

const inputClass =
  'w-full px-4 py-3 bg-light-surface dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-white placeholder-light-text-secondary dark:placeholder-dark-text-secondary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent';
const labelClass = 'block text-sm font-medium text-light-text dark:text-dark-text mb-2';

/** Validation errors arrive as a list of messages; show the first. */
const messageFrom = (err: any): string | undefined => {
  const message = err?.response?.data?.message;
  return Array.isArray(message) ? message[0] : message;
};

/**
 * Reset a forgotten password: ask for the email, then take the emailed 6-digit code and a
 * new password. The first step never says whether the email has an account.
 */
const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState<'email' | 'reset'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const sendCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError('');
    setBusy(true);
    try {
      await axios.post('/auth/forgot-password', { email: email.trim() });
      setStep('reset');
    } catch (err: any) {
      setError(
        messageFrom(err) || t('auth.forgot.sendFailed', "We couldn't send the code. Try again."),
      );
    } finally {
      setBusy(false);
    }
  };

  const reset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError(t('auth.forgot.tooShort', 'Use at least 8 characters.'));
      return;
    }
    if (password !== confirm) {
      setError(t('auth.forgot.mismatch', "The passwords don't match."));
      return;
    }
    setBusy(true);
    try {
      await axios.post('/auth/reset-password', {
        email: email.trim(),
        code: code.trim(),
        newPassword: password,
      });
      toast.success(t('auth.forgot.done', 'Password changed. Sign in with your new password.'));
      navigate('/login');
    } catch (err: any) {
      setError(
        messageFrom(err) || t('auth.forgot.resetFailed', "We couldn't reset your password."),
      );
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg flex flex-col">
      <div className="flex-1 flex flex-col max-w-mobile md:max-w-desktop mx-auto w-full">
        <div className="flex items-center gap-4 p-4">
          <button
            type="button"
            onClick={() => (step === 'reset' ? setStep('email') : navigate('/login'))}
            aria-label={t('common.back', 'Back')}
            className="p-2 hover:bg-light-surface dark:hover:bg-dark-surface rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 text-light-text dark:text-dark-text" />
          </button>
        </div>

        <div className="flex-1 flex flex-col justify-center px-6 pb-8">
          <div className="text-center mb-8">
            <BrandLogo
              imgClassName="h-16 w-auto mx-auto mb-4"
              textClassName="block mb-4 text-3xl font-bold tracking-tight text-light-text dark:text-dark-text"
            />
            <h1 className="font-cormorant text-3xl font-bold text-light-text dark:text-dark-text mb-2">
              {t('auth.forgot.title', 'Reset your password')}
            </h1>
            <p className="text-light-text-secondary dark:text-dark-text-secondary">
              {step === 'email'
                ? t(
                    'auth.forgot.intro',
                    "Enter your account's email and we'll send you a 6-digit code.",
                  )
                : t(
                    'auth.forgot.sent',
                    'If {{email}} has a Plotweave password, a code is on its way. It works for 15 minutes.',
                    { email: email.trim() },
                  )}
            </p>
          </div>

          {error && (
            <div
              role="alert"
              className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl"
            >
              <p className="text-red-700 dark:text-red-300 text-sm text-center">{error}</p>
            </div>
          )}

          {step === 'email' ? (
            <form onSubmit={sendCode} className="space-y-4">
              <div>
                <label htmlFor="email" className={labelClass}>
                  {t('auth.email', 'Email')}
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass}
                  placeholder={t('auth.emailPlaceholder', 'you@example.com')}
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="w-full py-4 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 text-white font-semibold rounded-xl transition-colors disabled:cursor-not-allowed"
              >
                {t('auth.forgot.sendCode', 'Send code')}
              </button>
            </form>
          ) : (
            <form onSubmit={reset} className="space-y-4">
              <div>
                <label htmlFor="code" className={labelClass}>
                  {t('auth.forgot.codeLabel', 'Code from the email')}
                </label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className={`${inputClass} tracking-widest text-center`}
                  autoFocus
                />
              </div>
              <div>
                <label htmlFor="new-password" className={labelClass}>
                  {t('auth.forgot.newPassword', 'New password')}
                </label>
                <input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="confirm-password" className={labelClass}>
                  {t('auth.forgot.confirmPassword', 'Repeat the new password')}
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className={inputClass}
                />
              </div>
              <button
                type="submit"
                disabled={busy || code.length !== 6}
                className="w-full py-4 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 text-white font-semibold rounded-xl transition-colors disabled:cursor-not-allowed"
              >
                {t('auth.forgot.reset', 'Change password')}
              </button>
              <button
                type="button"
                onClick={() => sendCode()}
                disabled={busy}
                className="w-full text-sm underline text-light-text-secondary dark:text-dark-text-secondary"
              >
                {t('auth.forgot.resend', 'Send a new code')}
              </button>
            </form>
          )}

          <div className="text-center mt-6">
            <Link
              to="/login"
              className="text-primary-500 hover:text-primary-400 font-medium transition-colors"
            >
              {t('auth.forgot.backToSignIn', 'Back to sign in')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
